import { useQueryClient } from "@tanstack/react-query";

import type { UserInputAttachedFile } from "@intelligems/ig-types/schemas/ai/node-execution";
import type { AgenticLogicDataSurface } from "@intelligems/ig-utils/agentic-logic-code-snippets";

import { getAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { getAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { agenticLogicKeys } from "@src/api/queryKeys";

import type { AgenticLogicCreationInitialContext } from "./agenticLogicCreationContext";
import { buildCodeEntryCreationContext } from "./agenticLogicCreationContext";

/**
 * The one place a creation context is built for the acting user.
 *
 * Every entry into the authoring chat — the create page's "Build", the idle
 * rail's first message, the repair panel's counter-proposal — has to type the
 * SDK the way Monaco does for the same user: an Intelligems admin gets
 * `sdk.getDataset()` over the org's keys and `sdk.getSource()`, and a merchant
 * gets neither. The caller passes that answer in, read from the same
 * `useAgenticLogicDataSurface` as the rule workspace, so the two share one
 * react-query cache entry and the chat costs no extra request.
 *
 * Deliberately NOT gated on the surface settling. Unlike the Monaco path —
 * which must gate, because a premature mount red-squiggles an existing rule's
 * code — the snapshot here is documentation for the model, and an admin's
 * model also holds the live `list_agentic_logic_datasets` read. Gating the
 * always-open rail's composer on a metadata query would be the worse failure.
 *
 * The builder is ASYNC because of the second read: a session opened on an
 * existing rule must carry that rule's current copy, source and graph, or the
 * authoring node can only re-derive the whole file from the rule's name and
 * description. On the workspace the draft key is already observed, so this is
 * almost always a cache hit and costs no request; when it is not, awaiting is
 * required — sending an edit on an unsettled draft is precisely the bug. A
 * failed read REJECTS rather than falling back to a blank-starter edit
 * context: a refused edit is recoverable, a silent full rewrite is not.
 */
export const useAgenticLogicCreationContextBuilder = (
  dataSurface: AgenticLogicDataSurface
) => {
  const queryClient = useQueryClient();

  return async ({
    prompt,
    files,
    existingRuleId,
  }: {
    prompt: string;
    files?: UserInputAttachedFile[];
    existingRuleId?: string;
  }): Promise<AgenticLogicCreationInitialContext> => {
    if (!existingRuleId) {
      return buildCodeEntryCreationContext({ prompt, files, dataSurface });
    }

    // Both reads, together: which KIND of rule this is decides the whole edit
    // contract, and a template rule's draft is always null — indistinguishable
    // from a code rule nobody has saved yet. On the workspace both keys are
    // already observed, so this is normally two cache hits.
    const [{ draft }, { rule }] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: agenticLogicKeys.draft(existingRuleId),
        queryFn: async () =>
          getAgenticLogicRuleDraft({ ruleId: existingRuleId }),
      }),
      queryClient.ensureQueryData({
        queryKey: agenticLogicKeys.rule(existingRuleId),
        queryFn: async () => getAgenticLogicRule({ ruleId: existingRuleId }),
      }),
    ]);

    return buildCodeEntryCreationContext({
      prompt,
      files,
      existingRule: {
        ruleId: existingRuleId,
        managedBy: rule.managedBy,
        name: rule.name,
        description: rule.description,
        draft,
      },
      dataSurface,
    });
  };
};
